import React, { useEffect, useState } from 'react'
import produce from 'immer'
import { length } from 'stringz'

type InputInitialState = {
  id: string
  defaultValue: string
  initialValue: string
  onChange: (value: string) => void
}

type InputFieldState = {
  isDirty: boolean

  /**
   * input field 的值 (controlled)
   */
  value: string

  /**
   * 去除 CJK IME 未完成的計數
   */
  count: number

  /**
   * 判斷是否為 CJK IME (中日韓輸入法) 模式
   */
  isComposingText: boolean

  /**
   * input field 狀態
   *
   * 參考 https://www.figma.com/file/rZn3HX820cCfp6ecXV5BXc/Web-Editor-Redesign-%E7%B7%A8%E8%BC%AF%E5%99%A8%E9%87%8D%E6%A7%8B-Stage1-and-2-11915?node-id=5950%3A27373
   */
  status: 'default' | 'empty' | 'focus' | 'ideal' | 'maximum'
}

export function useInputState(inputTuples: InputInitialState[]) {
  const [inputStates, setInputState] = useState<InputFieldState[]>(() =>
    inputTuples.map(({ defaultValue, initialValue }) => ({
      value: initialValue || defaultValue,
      count: length(initialValue || defaultValue),
      status: initialValue ? 'ideal' : 'empty',
      isComposingText: false,
      isDirty: false,
    })),
  )

  useEffect(() => {
    for (const [idx, { onChange }] of inputTuples.entries()) {
      if (inputStates[idx].isDirty) {
        onChange(inputStates[idx].value)
        setInputState(
          produce((draft: InputFieldState[]) => {
            draft[idx].isDirty = false
          }),
        )
      }
    }
  }, [inputStates, inputTuples])

  const handleInputChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const value = event.currentTarget.value
    const maxLength = event.currentTarget.maxLength
    const idx = inputTuples.findIndex(
      (state) => state.id === event.currentTarget.getAttribute('id'),
    )

    setInputState(
      produce((draft: InputFieldState[]) => {
        if (length(value) === 0) {
          draft[idx].status = 'empty'
        } else if (length(value) >= maxLength) {
          draft[idx].status = 'maximum'
        } else {
          draft[idx].status = 'focus'
        }

        draft[idx].isDirty = true
        draft[idx].value = value
      }),
    )
  }

  /**
   * CJK (中日韓文) 在 IME (輸入法) 模式的時候不計算字數，等到輸入完成再做
   */
  const handleComposingUpdate = (event: React.CompositionEvent<HTMLTextAreaElement>) => {
    const idx = inputTuples.findIndex(
      (state) => state.id === event.currentTarget.getAttribute('id'),
    )

    setInputState(
      produce((draft: InputFieldState[]) => {
        if (event.type === 'compositionstart') {
          draft[idx].isComposingText = true
        }
        if (event.type === 'compositionend') {
          draft[idx].isComposingText = false
        }
      }),
    )
  }

  useEffect(() => {
    for (const [idx] of inputTuples.entries()) {
      if (inputStates[idx].isComposingText === false) {
        setInputState(
          produce((draft: InputFieldState[]) => {
            draft[idx].count = length(draft[idx].value)
          }),
        )
      }
    }
  }, [inputStates, inputTuples])

  const handleFocusChange = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    const maxLength = event.currentTarget.maxLength
    const idx = inputTuples.findIndex(
      (state) => state.id === event.currentTarget.getAttribute('id'),
    )
    setInputState(
      produce((draft: InputFieldState[]) => {
        const count = length(draft[idx].value)
        if (event.type === 'blur') {
          draft[idx].status = count === 0 ? 'empty' : count === maxLength ? 'maximum' : 'ideal'
        }
        if (event.type === 'focus') {
          draft[idx].status = count === maxLength ? 'maximum' : 'focus'
        }
      }),
    )
  }

  return [inputStates, handleInputChange, handleComposingUpdate, handleFocusChange] as const
}
